import React, { useMemo } from 'react'
import AceEditor from 'react-ace'
import { useTheme } from 'styled-components'

import 'ace-builds/src-noconflict/mode-mysql'
import 'ace-builds/src-noconflict/theme-chrome'
import { AceEditorWrapper } from 'core/sql-editor/SqlEditor.styles'
import { AdvancedQueryTablesView } from 'modules/cohort-browser/components/phenotypic-data-discovery/table-discovery/query-tables-view/AdvancedQueryTablesView'
import { convertQueryArrayToElements } from 'modules/cohort-browser/components/query/renderUtils'
import { AppliedQueryType } from 'modules/cohort-browser/enums'

import {
  PhenotypicDisplaySql,
  PhenotypicDisplayStandard,
  PhenotypicDisplayTable,
  QueryChipsContainer
} from './PreviewQuery.styles'
import type { AnyAppliedQuery, PreviewQueryTag, QueryElement, TablesQuery } from './types'

interface PreviewQueryProps {
  mode: AppliedQueryType
  query?: AnyAppliedQuery
  testId?: string
}

export const PreviewQuery = ({ query, mode, testId }: PreviewQueryProps): JSX.Element | null => {
  const theme = useTheme()

  const standardModeQueryAsElements = useMemo((): QueryElement[] => {
    if (Array.isArray(query)) {
      return convertQueryArrayToElements(query as PreviewQueryTag[])
    }

    return []
  }, [query])

  if (!query) {
    return null
  }

  if (mode === AppliedQueryType.Standard && (query as PreviewQueryTag[]).length > 0) {
    return (
      <PhenotypicDisplayStandard data-testid={testId ?? 'phenotypic-container'}>
        {standardModeQueryAsElements.map((elem: QueryElement) => (
          <QueryChipsContainer key={elem.key}>{elem.component}</QueryChipsContainer>
        ))}
      </PhenotypicDisplayStandard>
    )
  }

  if (mode === AppliedQueryType.Sql) {
    return (
      <PhenotypicDisplaySql data-testid={testId}>
        <AceEditorWrapper data-testid="sql-editor-input">
          <AceEditor
            mode="mysql"
            name="sql-query"
            theme="chrome"
            fontSize={14}
            showPrintMargin={false}
            showGutter={true}
            highlightActiveLine={true}
            value={query as string}
            setOptions={{
              tabSize: 2,
              readOnly: true
            }}
            style={{ backgroundColor: theme.color.background.main }}
            height="100px"
            width="100%"
          />
        </AceEditorWrapper>
      </PhenotypicDisplaySql>
    )
  }

  if (mode === AppliedQueryType.Tables) {
    return (
      <PhenotypicDisplayTable data-testid={testId}>
        <AdvancedQueryTablesView query={query as TablesQuery} />
      </PhenotypicDisplayTable>
    )
  }

  return null
}
